import { useState } from 'react';
import { Sparkle, Tag, NotePencil, GraduationCap, Info } from '@phosphor-icons/react';
import { CategoriesTab } from './ai-settings/CategoriesTab';
import { PromptTab } from './ai-settings/PromptTab';
import { RulesTab } from './ai-settings/RulesTab';
import type { AiSettingsKind } from './ai-settings/promptApi';

function InfoTooltip({ text }: { text: string }) {
  return (
    <div className="group/tip relative inline-flex ml-1.5 align-middle">
      <Info size={14} weight="bold" className="text-slate-400 hover:text-accent cursor-help transition-colors" />
      <div className="pointer-events-none absolute z-50 bottom-full right-1/2 translate-x-1/2 mb-2 w-64 opacity-0 scale-95 group-hover/tip:opacity-100 group-hover/tip:scale-100 transition-all duration-150 origin-bottom">
        <div className="bg-slate-900 text-white text-[11px] font-medium leading-relaxed rounded-lg px-3 py-2 shadow-xl text-center">
          {text}
        </div>
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
      </div>
    </div>
  );
}

type SettingsTab = 'categories' | 'prompt' | 'rules';

const TABS: { id: SettingsTab; label: string; icon: typeof Tag }[] = [
  { id: 'categories', label: 'Categories', icon: Tag },
  { id: 'prompt', label: 'Prompt', icon: NotePencil },
  { id: 'rules', label: 'Learned Rules', icon: GraduationCap },
];

/** Projects: categories, prompt and learned rules. Proposals: prompt and learned rules
 * (their domain lists come from the proposals themselves). */
export const AISettingsView = ({ kind = 'project' }: { kind?: AiSettingsKind }) => {
  const tabs = kind === 'proposal' ? TABS.filter((t) => t.id !== 'categories') : TABS;
  const [tab, setTab] = useState<SettingsTab>(tabs[0].id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <h1 className="text-2xl font-black text-slate-900 font-outfit flex items-center gap-2">
            <Sparkle size={28} className="text-accent" weight="fill" />
            {kind === 'proposal' ? 'Proposal AI Settings' : 'AI Settings'}
          </h1>
          <InfoTooltip text={kind === 'proposal'
            ? 'Manage how the AI fills in a new proposal from its document. You can edit the prompt and review the rules the AI has learned from the changes reviewers made to what it filled in.'
            : 'Manage how the AI classifies projects. You can view the base prompt, modify available categories, and review the rules the AI has learned from past human corrections.'} />
        </div>
      </div>

      {/* Tab pills */}
      <div className="inline-flex items-center gap-1 bg-slate-100/80 border border-slate-200/60 rounded-2xl p-1.5 overflow-x-auto no-scrollbar">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-200 ${
                tab === t.id
                  ? 'bg-white text-slate-900 shadow-[0_2px_10px_rgba(15,23,42,0.12)]'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <Icon size={16} weight={tab === t.id ? 'fill' : 'regular'} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="animate-in fade-in duration-300">
        {tab === 'categories' && <CategoriesTab />}

        {tab === 'prompt' && <PromptTab kind={kind} />}

        {tab === 'rules' && <RulesTab kind={kind} />}
      </div>
    </div>
  );
};
